import { copilotRepository } from '@/service/repository/copilotRepository';
import store from '@/store';
import { DateTime } from 'luxon';

export class WalkingService {
	constructor() {}

	/**
	 * @param {object} data
	 * @param {{ openCommanderOnSuccess?: boolean, optimisticPlaceholder?: boolean }} [options]
	 *   optimisticPlaceholder: when true (default outside TPV), shows a fictitious loading card
	 *   in Sala lists until createWalkin returns, then swaps in the real reservation.
	 */
	async create(data, { openCommanderOnSuccess = false, optimisticPlaceholder } = {}) {
		const isTakeaway =
			data?.room === 'Takeaway' ||
			data?.selectedZone === 'Takeaway' ||
			data?.type === 'takeaway';
		const useOptimistic =
			(optimisticPlaceholder ?? store.getters['copilot/view'] !== 'TPV') && !isTakeaway;

		let fictitiousId = null;
		if (useOptimistic) {
			const fictitiousAccount = this._buildFictitiousAccount(data);
			fictitiousId = fictitiousAccount.id;
			store.commit('copilot/addFictitiousAccount', fictitiousAccount);
		}

		try {
			const createWalkInResult = await copilotRepository.createWalkin(data);

			if (createWalkInResult.result === 0) {
				const reservation = createWalkInResult.reservation;
				// addReservationToReservations drops matching fictitious cards first (also covers WS race)
				if (reservation && !isTakeaway) {
					store.commit('copilot/addReservationToReservations', { reservation });
				} else if (fictitiousId != null) {
					store.commit('copilot/removeFictitiousAccount', fictitiousId);
					fictitiousId = null;
				}
				store.commit('reservation/setReservation', reservation);
				store.commit('copilot/setCommanderReservation', { commanderReservation: reservation });
				if (openCommanderOnSuccess && store.getters['copilot/view'] !== 'TPV') {
					store.dispatch('uiOverlay/setIsShowOverlayCommander', true);
				}
			}

			if (fictitiousId != null) {
				store.commit('copilot/removeFictitiousAccount', fictitiousId);
			}

			return createWalkInResult;
		} catch (error) {
			if (fictitiousId != null) {
				store.commit('copilot/removeFictitiousAccount', fictitiousId);
			}
			throw error;
		}
	}

	async createKiosk(data) {
		let createWalkInResult = await copilotRepository.createKioskoWalkin(data);

		if (createWalkInResult.result === 0) {
			let reservation = createWalkInResult.reservation;
			store.commit('reservation/setReservation', reservation);

			this._openQRInvitation(reservation.id);
		}

		return createWalkInResult;
	}

	/**
	 * Placeholder card for Sala while createWalkin is in flight (same idea as TPV fictitious accounts).
	 * Fields must match sort keys used by `sortAccountsByAreaFilter` (startDate HH:mm, table name)
	 * and zone grouping (`roomName`) so the skeleton sits where the real account will land.
	 * @param {object} data createWalkin payload
	 */
	_buildFictitiousAccount(data) {
		const fictitiousId = `fictitious_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
		const nowLocal = DateTime.local();
		const startDate = nowLocal.toFormat('HH:mm');
		const startDateTime = nowLocal.toFormat('yyyy-MM-dd HH:mm:ss');

		const venueTables = store.getters['copilot/tables'] || [];
		const rawTableIds = Array.isArray(data?.tables) ? data.tables : [];
		const matchedTables = this._matchVenueTables(venueTables, rawTableIds);

		const firstTable = matchedTables[0] || null;
		const roomName =
			data?.room ||
			data?.selectedZone ||
			firstTable?.room ||
			null;
		const roomDisplayName = firstTable?.roomLabel || roomName;

		// Same primary-table rule as sortAccountsByAreaFilter / getPrimaryTableName
		const tables = matchedTables.map((t) => ({
			id: t.table_id ?? t.id,
			name: t.name,
		}));
		const tableName = tables[0]?.name ?? null;

		return {
			id: fictitiousId,
			realId: fictitiousId,
			table: tableName,
			tables,
			roomName,
			roomDisplayName,
			locationTag: data?.locationTag ?? null,
			status: 1,
			menuAccessEnabled: 1,
			startDate,
			startDateTime,
			createdDateTime: startDateTime,
			paymentInfo: {
				total: 0,
				paid: 0,
				pending: 0,
			},
			name: data?.name ?? null,
			pax: data?.pax ?? null,
			isTest: Boolean(data?.isTest),
			isFictitious: true,
			isLoading: true,
			reservationUsers: [],
			cartSession: null,
			finishedDateTime: null,
			type: 'local',
			serviceMode: 'located',
		};
	}

	/**
	 * Resolve createWalkin table ids (numeric table_id or uuid) against venue tables.
	 * Preserves payload order so tables[0] matches the primary table used for sorting.
	 * @param {object[]} venueTables
	 * @param {unknown[]} rawTableIds
	 * @returns {object[]}
	 */
	_matchVenueTables(venueTables, rawTableIds) {
		if (!rawTableIds.length || !venueTables.length) {
			return [];
		}

		const matched = [];
		for (const rawId of rawTableIds) {
			const idStr = String(rawId);
			const found = venueTables.find((t) => {
				const candidates = [t.table_id, t.id, t.name];
				return candidates.some((c) => c != null && String(c) === idStr);
			});
			if (found) {
				matched.push(found);
			}
		}
		return matched;
	}

	_openQRInvitation(reservationId) {
		const reservationElementId = 'reservation-' + reservationId;

		let reservationElement = document.getElementById(reservationElementId);

		if (reservationElement) {
			this._showQrModal(reservationId, reservationElementId);
			return;
		}

		const observer = new MutationObserver((mutationsList, observer) => {
			reservationElement = document.getElementById(reservationElementId);

			if (reservationElement) {
				observer.disconnect(); // Stop observing once the element is found
				this._showQrModal(reservationId, reservationElementId);
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });

		setTimeout(() => {
			observer.disconnect();
			reservationElement = document.getElementById(reservationElementId);
			if (!reservationElement) {
				console.log(`Reservation element for reservation ${reservationId} was not found after 3 seconds.`);
			}
		}, 3000);
	}

	_showQrModal(reservationId, reservationElementId) {
		const reservationElement = document.getElementById(reservationElementId);

		// Buscar el accordion padre usando el elemento de reservación
		const parentAccordion = reservationElement?.closest('[data-bs-parent="#accordionAllServices"]');

		if (parentAccordion && !parentAccordion.classList.contains('show')) {
			parentAccordion.classList.add('show');
		}

		if (reservationElement) {
			window.scrollTo({
				top: reservationElement.offsetTop,
				behavior: 'smooth',
			});
		} else {
			console.error(`Reservation element with ID '${reservationElementId}' was not found.`);
		}

		// setTimeout(() => {
		// 	showModal("modalQr" + reservationId);
		// }, 0);
		store.dispatch('uiOverlay/setIsShowOverlayQRAccount', true);
	}
}

export const walkingService = new WalkingService();
